import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, CircularProgress, Divider, makeStyles, Paper, Step, StepLabel, Stepper } from '@material-ui/core';
import { StepIconProps } from '@material-ui/core/StepIcon';
import { CheckRounded as CheckIcon } from '@material-ui/icons';

import { Organization } from '../../../organizations/types/organization.type';
import { QuoteDeliveryInput, QuoteGlobalDiscount, QuoteItem } from '../../types/quote.type';
import { CatalogStep } from './CatalogStep.component';
import { ConfigurationStep } from './ConfigurationStep.component';
import { SummaryStep } from './SummaryStep.component';

export type CreateQuoteFormValues = {
  items: QuoteItem[];
  globalDiscount?: QuoteGlobalDiscount;
  delivery: QuoteDeliveryInput;
  validUntil: string;
  conditions?: string;
  ownerId: string;
};

type Props = {
  organization: Organization;
  defaultValues?: Partial<CreateQuoteFormValues>;
  onSubmit: (values: CreateQuoteFormValues) => Promise<void> | void;
  isSubmitting?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
};

const STEPS = ['Catalogue', 'Configuration', 'Récapitulatif'];

function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDefaultFormValues(): CreateQuoteFormValues {
  return {
    items: [],
    globalDiscount: { value: 0, reduction: 'percent' },
    delivery: { date: addDaysIso(7), schedule: '08H00-18H00', deliveryPointId: '' },
    validUntil: addDaysIso(30),
    conditions: '',
    ownerId: '',
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
  },
  stepperContainer: {
    width: '80%',
    alignSelf: 'center',
  },
  stepper: {
    padding: '0.5rem 0 1rem 0',
  },
  stepIcon: (props: { active: boolean; completed: boolean }) => ({
    width: 28,
    height: 28,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 14,
    backgroundColor: props.active || props.completed ? theme.palette.admin.primary.main : '#e0e0e0',
    color: props.active || props.completed ? theme.palette.admin.primary.contrastText : '#757575',
  }),
  stepIconCheck: {
    fontSize: 18,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    paddingTop: '0.5rem',
  },
  primaryButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.admin.primary.light,
      color: theme.palette.admin.primary.contrastText,
    },
  },
}));

function QuoteStepIcon(props: StepIconProps) {
  const { active = false, completed = false, icon } = props;
  const classes = useStyles({ active, completed });
  return <div className={classes.stepIcon}>{completed ? <CheckIcon className={classes.stepIconCheck} /> : icon}</div>;
}

function mergeDefaults(partial?: Partial<CreateQuoteFormValues>): CreateQuoteFormValues {
  const defaults = getDefaultFormValues();
  return {
    ...defaults,
    ...partial,
    delivery: { ...defaults.delivery, ...(partial?.delivery ?? {}) },
    items: partial?.items ?? defaults.items,
    globalDiscount: partial?.globalDiscount ?? defaults.globalDiscount,
  };
}

export function CreateQuoteStepper({
  organization,
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Créer',
  submittingLabel = 'Création…',
}: Props) {
  const classes = useStyles({ active: false, completed: false });
  const [activeStep, setActiveStep] = useState(0);

  const methods = useForm<CreateQuoteFormValues>({
    defaultValues: mergeDefaults(defaultValues),
    mode: 'onChange',
  });

  const { control, handleSubmit, trigger, watch, setValue, getValues } = methods;

  const handleNext = async () => {
    if (activeStep === 0) {
      const items = getValues('items');
      if (!items || items.length === 0) {
        await trigger('items');
        return;
      }
    }

    if (activeStep === 1) {
      const isValid = await trigger(['delivery.date', 'delivery.schedule', 'delivery.deliveryPointId', 'validUntil']);
      if (!isValid) return;

      const { delivery, validUntil } = getValues();
      if (!delivery?.date || !delivery?.schedule || !delivery?.deliveryPointId || !validUntil) return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => Math.max(0, prev - 1));

  const submit = handleSubmit(async (values) => {
    const discountValue = Number(values.globalDiscount?.value) || 0;
    const globalDiscount = discountValue > 0 ? values.globalDiscount : undefined;
    await onSubmit({ ...values, globalDiscount });
  });

  const isLastStep = activeStep === STEPS.length - 1;

  const watchedItems = watch('items') ?? [];
  const watchedDelivery = watch('delivery');
  const watchedValidUntil = watch('validUntil');
  const watchedOwnerId = watch('ownerId');

  const isNextDisabled = (() => {
    if (activeStep === 0) {
      return watchedItems.length === 0;
    }
    if (activeStep === 1) {
      return (
        !watchedDelivery?.date ||
        !watchedDelivery?.schedule ||
        watchedDelivery.schedule.trim().length === 0 ||
        !watchedDelivery?.deliveryPointId ||
        !watchedValidUntil ||
        !watchedOwnerId
      );
    }
    return false;
  })();

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.stepperContainer}>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={QuoteStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>

      <Divider />

      <section className={classes.content}>
        {activeStep === 0 && <CatalogStep control={control} watch={watch} setValue={setValue} />}
        {activeStep === 1 && (
          <ConfigurationStep
            organization={organization}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        )}
        {activeStep === 2 && <SummaryStep organization={organization} watch={watch} />}
      </section>

      <section className={classes.actions}>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Précédent
          </Button>
        )}
        {!isLastStep && (
          <Button
            variant="contained"
            className={classes.primaryButton}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            Suivant
          </Button>
        )}
        {isLastStep && (
          <Button
            variant="contained"
            className={classes.primaryButton}
            onClick={submit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        )}
      </section>
    </Paper>
  );
}
